import * as d3 from 'd3';
import {
  centerTranslate,
  fitScale as fitScaleMath,
  fitTranslate,
  isFinitePoint,
  openScale,
  reanchoredTranslate,
  SCALE_MAX,
  SCALE_MIN
} from './viewportMath';
import { visibleCenter } from './openFrame';

function zooming(e, opts, pedref) {
  let t = e.transform;
  let k = t.k && t.k !== 1 ? t.k : 1;

  let ped = d3.select(pedref.current).select('.diagram');
  ped.attr(
    'transform',
    'translate(' + t.x + ',' + t.y + ')' + (k ? ' scale(' + k + ')' : '')
  );

  const pattern = d3.select(pedref.current).select('pattern');

  pattern.attr(
    'patternTransform',
    'translate(' + t.x + ',' + t.y + ')' + (k ? ' scale(' + k + ')' : '')
  );
}

const viewportSize = (ref) => ({
  vw: ref.current?.offsetWidth || 1140,
  vh: ref.current?.offsetHeight || 860
});

const diagramBBox = (svg) => {
  try {
    const diagram = svg.select('.diagram').node();
    return diagram ? diagram.getBBox() : null;
  } catch (e) {
    return null;
  }
};

// Transform that fits the whole diagram into the viewport, centred — clamped to
// the zoom extent. Falls back to the legacy default if the diagram bbox isn't
// measurable yet (e.g. jsdom).
const fitToContentTransform = (svg, ref) => {
  const { vw, vh } = viewportSize(ref);
  const bbox = diagramBBox(svg);
  const k = fitScaleMath(vw, vh, bbox);
  if (bbox && k != null) {
    const { x, y } = fitTranslate(vw, vh, bbox, k);
    return d3.zoomIdentity.translate(x, y).scale(k);
  }
  // Legacy fallback (content not measurable yet).
  return d3.zoomIdentity.scale(0.25).translate(1 + vw / 4, 1);
};

// First-open transform CENTRED ON A GIVEN POINT (the proband/focused person's layout
// coords), so a pedigree opens on the person the clinician cares about rather than the
// geometric middle of the whole tree. Scale = min(fitScale, STANDARD) so a large family
// still opens with context and a small one isn't over-zoomed. Falls back to a plain
// fit-to-content when the centre isn't known or the bbox isn't measurable.
// `inset` is the width of the detail drawer floating over the right of the canvas:
// the point is centred in the UNCOVERED area (openFrame.js visibleCenter).
const centeredOnPointTransform = (svg, ref, center, inset = 0) => {
  if (!isFinitePoint(center)) {
    return fitToContentTransform(svg, ref);
  }
  const { vw, vh } = viewportSize(ref);
  const k = openScale(fitScaleMath(vw, vh, diagramBBox(svg)));
  const { x, y } = centerTranslate(
    vw,
    vh,
    center,
    k,
    visibleCenter(vw, vh, inset)
  );
  return d3.zoomIdentity.translate(x, y).scale(k);
};

// `opts.fit` — when true (first open of a pedigree), ignore any persisted zoom and
// open centred on the focus person (`opts.center`, the proband's layout coords) so it
// never opens to blank space; when false (a re-render after an edit), restore the live
// view so edits don't snap the user's pan/zoom back. The persisted 'zoom' is still
// written on every interaction (for that re-render restore) but is NOT used as the open
// transform — so one pedigree's zoom never carries into the next.
//
// `opts.center` — {x,y} layout coords of the person to centre on first open (proband /
// focused). `opts.inset` — the drawer width covering the right of the canvas, so the
// centre lands in the uncovered area. `opts.onUserGesture` — called for a zoom event
// the CLINICIAN made (wheel, drag, touch, double-click: `event.sourceEvent` is set;
// a programmatic `zoom.transform` has none), which is what ends the open-time
// framing (TGCLOUD-1863). `opts.reanchorWorldDelta` — {dx,dy} the layout-coord shift
// of the node the user was editing since the previous layout; on a re-render we
// translate the restored view by -k·delta so that node keeps the SAME screen position
// while the tree grows around it ("don't lose focus of the node you're working on").
export const initZoom = (options, svg, ref, opts = {}) => {
  // Persist the live view under a PER-PEDIGREE key so that, with several charts
  // mounted at once (multi-pedigree-tabs workspace), one chart's pan/zoom never
  // overwrites another's restore point. `opts.zoomKey` is `zoom:<familyId>` from
  // the caller; it falls back to the legacy global 'zoom' key when unset so a lone
  // chart with no key behaves exactly as before.
  const zoomKey = opts.zoomKey || 'zoom';
  const zoom = d3
    .zoom()
    .scaleExtent([SCALE_MIN, SCALE_MAX])
    .filter((event) => {
      // Reserve shift+mousedown on the canvas background for lasso selection;
      // everything else falls back to the default d3-zoom filter.
      if (
        event.type === 'mousedown' &&
        event.shiftKey &&
        event.target?.getAttribute?.('class') === 'canvas'
      ) {
        return false;
      }
      return (!event.ctrlKey || event.type === 'wheel') && !event.button;
    })
    .on('zoom', (event) => {
      zooming(event, options, ref);
      localStorage.setItem(zoomKey, JSON.stringify(event.transform));
      if (event.sourceEvent && typeof opts.onUserGesture === 'function') {
        opts.onUserGesture(event);
      }
    });

  svg.call(zoom);

  let transform;
  if (opts.fit) {
    // First open (and the open-time relayouts until the view is touched): centre on
    // the focus person, regardless of any carried-over zoom; falls back to
    // fit-to-content when the centre isn't known.
    transform = centeredOnPointTransform(svg, ref, opts.center, opts.inset);
  } else {
    const stored_zoom = JSON.parse(localStorage.getItem(zoomKey));
    if (stored_zoom) {
      // Restore the live view, but keep the edited node fixed on screen by shifting
      // the view by -k·(world delta) so it doesn't drift as the tree grows around it.
      const { x, y, k } = reanchoredTranslate(
        stored_zoom,
        opts.reanchorWorldDelta
      );
      transform = d3.zoomIdentity.translate(x, y).scale(k);
    } else {
      transform = fitToContentTransform(svg, ref);
    }
  }

  svg.call(zoom.transform, transform);

  return zoom;
};
